import { useState } from "react"
import { Link, useForm, usePage } from "@inertiajs/react"
import { ExternalLink, Mail, User } from "lucide-react"

import GoogleIcon from "@assets/images/icons/google.svg"
import GravatarIcon from "@assets/images/icons/gravatar.svg"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"

function getInitials(name, email) {
  if (name) {
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }
  return email ? email[0].toUpperCase() : "?"
}

export function ProfileForm({ user, submitLabel }) {
  const { errors: sharedErrors, current_user, csrf_token } = usePage().props
  const { data, setData, patch, processing, errors: formErrors } = useForm({
    name: user?.name || "",
    email: user?.email || "",
  })

  const errors = { ...sharedErrors, ...formErrors }
  const profilePhotoUrl = current_user?.avatar_url
  const profileName = current_user?.name || data.name
  const profileEmail = current_user?.email || data.email
  const profileAltText = profileName || profileEmail || "User"
  const googleConnected = !!current_user?.google_connected
  const hasGoogleAvatar = !!current_user?.has_google_avatar
  const initials = getInitials(profileName, profileEmail)
  const [avatarStatus, setAvatarStatus] = useState("idle")

  function handleSubmit(e) {
    e.preventDefault()
    patch("/account")
  }

  return (
    <>
      {!googleConnected && (
        <form id="connect-google-form" action="/auth/google_oauth2" method="post" data-turbo="false">
          <input type="hidden" name="authenticity_token" value={csrf_token} />
        </form>
      )}

      <form onSubmit={handleSubmit} className="max-w-md">
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="name">Name</FieldLabel>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                type="text"
                id="name"
                className="pl-9"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
              />
            </div>
            {errors.name && <FieldError>{errors.name}</FieldError>}
          </Field>

          <Field>
            <FieldLabel htmlFor="email">Email</FieldLabel>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                type="email"
                id="email"
                className="pl-9"
                value={data.email}
                onChange={(e) => setData("email", e.target.value)}
              />
            </div>
            {errors.email && <FieldError>{errors.email}</FieldError>}
          </Field>

          <Field>
            <FieldLabel>Photo</FieldLabel>
            <div className="flex items-start gap-3">
              <div className="relative shrink-0">
                <Avatar className="size-12 rounded-lg">
                  <AvatarImage
                    src={profilePhotoUrl}
                    alt={profileAltText}
                    referrerPolicy="no-referrer"
                    onLoadingStatusChange={setAvatarStatus}
                  />
                  <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                </Avatar>
                {hasGoogleAvatar && (
                  <span className="absolute -bottom-1.5 -right-1.5 flex size-5 items-center justify-center rounded-full bg-white ring-2 ring-background" aria-hidden="true">
                    <GoogleIcon className="size-3.5" />
                  </span>
                )}
                {!hasGoogleAvatar && avatarStatus === "loaded" && (
                  <span className="absolute -bottom-1.5 -right-1.5 flex size-5 items-center justify-center rounded-full bg-white ring-2 ring-background" aria-hidden="true">
                    <GravatarIcon className="size-3.5" />
                  </span>
                )}
              </div>

              <div className="space-y-2 text-sm text-muted-foreground">
                {hasGoogleAvatar ? (
                  <Button variant="ghost" size="sm" asChild>
                    <a href="https://myaccount.google.com" target="_blank" rel="noreferrer">
                      Update your photo via Google
                      <ExternalLink className="size-3 shrink-0" />
                    </a>
                  </Button>

                ) : avatarStatus === "loaded" ? (
                  <Button variant="ghost" size="sm" asChild>
                    <a href="https://gravatar.com" target="_blank" rel="noreferrer">
                      Update your photo via Gravatar
                      <ExternalLink className="size-3 shrink-0" />
                    </a>
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" asChild>
                    <a href="https://gravatar.com" target="_blank" rel="noreferrer">
                      Add a photo via Gravatar
                      <ExternalLink className="size-3 shrink-0" />
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </Field>

          <Field className="w-fit">
            <Button type="submit" disabled={processing}>
              {processing ? "Saving..." : submitLabel}
            </Button>
          </Field>
        </FieldGroup>
      </form>
    </>
  )
}

export function WorkspaceForm({ user, submitLabel }) {
  const { errors: sharedErrors } = usePage().props
  const { data, setData, patch, processing, errors: formErrors } = useForm({
    workspace_slug: user?.workspace_slug || "",
  })
  const errors = { ...sharedErrors, ...formErrors }

  function handleSubmit(e) {
    e.preventDefault()
    patch("/account")
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-md">
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="workspace_slug">Workspace URL</FieldLabel>
          <Input
            type="text"
            id="workspace_slug"
            value={data.workspace_slug}
            onChange={(e) => setData("workspace_slug", e.target.value)}
          />
          <FieldDescription>Only lowercase letters, numbers, and hyphens</FieldDescription>
          {errors.workspace_slug && <FieldError>{errors.workspace_slug}</FieldError>}
        </Field>

        <Field className="w-fit">
          <Button type="submit" disabled={processing}>
            {processing ? "Saving..." : submitLabel}
          </Button>
        </Field>

        <Field>
          <Link href="/workspaces" className="text-blue-600 hover:underline">
            Manage Workspaces
          </Link>
        </Field>
      </FieldGroup>
    </form>

  )
}
